import React from 'react';
import { PiTreeStructureDuotone, PiAtomDuotone, PiBracketsCurlyDuotone } from 'react-icons/pi';
import { TbRobot } from 'react-icons/tb';
import { RiToolsFill } from 'react-icons/ri';
import { FileSearch } from 'lucide-react';

/**
 * The one span-kind vocabulary: colour, label and glyph for each kind.
 *
 * There used to be two colour maps that disagreed — this file drew `tool` blue
 * and `agent` amber in the trace tree, while spanKind.js drew them the other way
 * round in the spans table — so the same span changed colour between the table
 * and the drawer opened from it. Every consumer now reads this map.
 *
 * Colours are theme tokens, so each has a light and a dark value. `run` is the
 * run itself at the root of the trace tree: brand orange, so the trunk is never
 * mistaken for one of its agent spans.
 *
 * Glyphs were picked one by one across four libraries (Nov 2026), drawn bare —
 * no chip or tile behind them.
 */
const lucide = (C) => function LucideGlyph({ size }) {
  return <C size={size} strokeWidth={1.9} aria-hidden />;
};

export const KIND_COLORS = {
  run:       { color: 'var(--brand-primary)',     label: 'Run',       Glyph: PiTreeStructureDuotone },
  agent:     { color: 'var(--feedback-info)',     label: 'Agent',     Glyph: TbRobot },
  llm:       { color: 'var(--accent-llm)',        label: 'LLM',       Glyph: PiAtomDuotone },
  tool:      { color: 'var(--feedback-warning)',  label: 'Tool',      Glyph: RiToolsFill },
  retrieval: { color: 'var(--feedback-success)',  label: 'Retrieval', Glyph: lucide(FileSearch) },
  span:      { color: 'var(--text-secondary)',    label: 'Span',      Glyph: PiBracketsCurlyDuotone },
};

export function colorForKind(kind) {
  // `generic` is the legacy key for an unkinded span — fold it into `span`.
  return KIND_COLORS[kind] || KIND_COLORS.span;
}

/** The kind's glyph, in the kind's colour unless `color` says otherwise. */
export function SpanKindIcon({ kind, size = 14, color }) {
  const { Glyph, color: kindColor } = colorForKind(kind);
  return (
    <span className="inline-flex" style={{ color: color || kindColor }} aria-hidden>
      <Glyph size={size} />
    </span>
  );
}

export default function KindTag({ kind }) {
  const color = colorForKind(kind);
  return (
    <span
      className="inline-block flex-none rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
      style={{ color: color.color, backgroundColor: `color-mix(in srgb, ${color.color} 14%, transparent)` }}
    >
      {color.label}
    </span>
  );
}
